import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useToast } from '../context/ToastContext';
import Button from './ui/Button';
import FormField from './ui/FormField';
import MultiSelect, { MultiSelectOption, SelectId } from './MultiSelect';
import '../App.css';
import { translateError } from '../utils/errorMessages';

type Visibility = 'public' | 'internal' | 'hidden';
type JoinPolicy = 'open' | 'approval';

interface AssignableBucket {
  eventTemplates: { _id: number; name: string; activityName?: string | null }[];
  groups: { _id: number; name: string; activityName?: string | null }[];
  events: { _id: number; name: string; startTime?: string; activityName?: string | null }[];
  members: { _id: number; name: string; activityNames?: string[] }[];
}

type Step = 0 | 1 | 2 | 3 | 4 | 5;

/**
 * ActivityCreationWizard — six-step flow for creating a new activity and
 * atomically pulling existing content into it. Used by ActivityCreatePage.
 *
 * All state is local to this component; a single POST to
 * /api/activities/with-content is issued on confirm.
 */
const ActivityCreationWizard: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>(0);
  const [submitting, setSubmitting] = useState(false);

  // Basics
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('hidden');
  const [joinPolicy, setJoinPolicy] = useState<JoinPolicy>('approval');

  // Assignable content
  const [assignable, setAssignable] = useState<AssignableBucket | null>(null);
  const [assignableLoading, setAssignableLoading] = useState(true);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<SelectId[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<SelectId[]>([]);
  const [selectedEventIds, setSelectedEventIds] = useState<SelectId[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<SelectId[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.activities.getAssignableContent();
        setAssignable(res.data as AssignableBucket);
      } catch {
        setAssignable({ eventTemplates: [], groups: [], events: [], members: [] });
      } finally {
        setAssignableLoading(false);
      }
    })();
  }, []);

  const withCurrent = (name: string, current?: string | null) =>
    current ? `${name} — ${t('activity.wizard.currentlyIn', { activity: current })}` : name;

  const templateOptions = useMemo<MultiSelectOption[]>(
    () => (assignable?.eventTemplates ?? []).map(x => ({
      _id: x._id,
      name: withCurrent(x.name, x.activityName),
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignable],
  );
  const groupOptions = useMemo<MultiSelectOption[]>(
    () => (assignable?.groups ?? []).map(x => ({
      _id: x._id,
      name: withCurrent(x.name, x.activityName),
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignable],
  );
  const eventOptions = useMemo<MultiSelectOption[]>(
    () => (assignable?.events ?? []).map(x => {
      const dated = x.startTime ? `${x.name} (${new Date(x.startTime).toLocaleDateString()})` : x.name;
      return { _id: x._id, name: withCurrent(dated, x.activityName) };
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignable],
  );
  const memberOptions = useMemo<MultiSelectOption[]>(
    () => (assignable?.members ?? []).map(x => ({
      _id: x._id,
      name: x.activityNames && x.activityNames.length > 0
        ? `${x.name} — ${t('activity.wizard.currentlyIn', { activity: x.activityNames.join(', ') })}`
        : x.name,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignable],
  );

  const next = () => setStep(s => (s < 5 ? ((s + 1) as Step) : s));
  const back = () => setStep(s => (s > 0 ? ((s - 1) as Step) : s));

  const handleSubmit = async () => {
    if (!name.trim()) {
      addToast(t('activity.wizard.stepBasics') + ': ' + t('common.required'), 'warning');
      setStep(0);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.activities.createWithContent({
        name: name.trim(),
        description: description || undefined,
        visibility,
        joinPolicy,
        assign: {
          eventTemplateIds: selectedTemplateIds.map(Number),
          groupIds: selectedGroupIds.map(Number),
          eventIds: selectedEventIds.map(Number),
          memberIds: selectedMemberIds.map(Number),
        },
        // Always force-reassign: the wizard explicitly surfaces "(currently in X)"
        // badges so picking an already-tagged item is an intentional move.
        force: true,
      });
      const data = res.data as any;
      addToast(
        t('activity.wizard.createSuccess', {
          templates: data.assigned?.templates ?? 0,
          groups: data.assigned?.groups ?? 0,
          events: data.assigned?.events ?? 0,
          members: data.assigned?.members ?? 0,
        }),
        'success',
      );
      navigate('/admin/activities');
    } catch (err: any) {
      const msg = translateError(err, t);
      addToast(msg || t('common.error'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Step header with progress indicator
  const renderHeader = () => (
    <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
      {[
        t('activity.wizard.stepBasics'),
        t('activity.wizard.stepTemplates'),
        t('activity.wizard.stepGroups'),
        t('activity.wizard.stepEvents'),
        t('activity.wizard.stepMembers'),
        t('activity.wizard.stepReview'),
      ].map((label, idx) => (
        <span
          key={label}
          style={{
            padding: '0.25rem 0.5rem',
            fontSize: '0.85rem',
            borderBottom: idx === step ? '2px solid var(--orange-primary)' : '2px solid transparent',
            color: idx <= step ? 'var(--text-primary)' : 'var(--text-muted)',
            fontWeight: idx === step ? 600 : 400,
          }}
        >
          {idx + 1}. {label}
        </span>
      ))}
    </div>
  );

  const renderNav = (canNext: boolean = true) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
      <Button variant="secondary" onClick={back} disabled={step === 0 || submitting}>
        {t('activity.wizard.back')}
      </Button>
      {step < 5 ? (
        <Button onClick={next} disabled={!canNext || submitting}>
          {t('activity.wizard.next')}
        </Button>
      ) : (
        <Button onClick={handleSubmit} loading={submitting}>
          {t('activity.wizard.confirm')}
        </Button>
      )}
    </div>
  );

  if (assignableLoading) {
    return <p>{t('common.loading')}</p>;
  }

  return (
    <div className="form-container">
      {renderHeader()}

      {step === 0 && (
        <div>
          <p className="form-help-text">{t('activity.wizard.basicsHint')}</p>
          <FormField htmlFor="activity-name" label={t('activities.name')} required>
            <input
              id="activity-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={100}
            />
          </FormField>
          <FormField htmlFor="activity-description" label={t('activities.description')}>
            <textarea
              id="activity-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
            />
          </FormField>
          <FormField htmlFor="activity-visibility" label={t('activities.visibility')}>
            <select
              id="activity-visibility"
              value={visibility}
              onChange={e => setVisibility(e.target.value as Visibility)}
            >
              <option value="public">{t('activities.visibilityPublic')}</option>
              <option value="internal">{t('activities.visibilityInternal')}</option>
              <option value="hidden">{t('activities.visibilityHidden')}</option>
            </select>
          </FormField>
          <FormField htmlFor="activity-joinpolicy" label={t('activities.joinPolicy')}>
            <select
              id="activity-joinpolicy"
              value={joinPolicy}
              onChange={e => setJoinPolicy(e.target.value as JoinPolicy)}
            >
              <option value="open">{t('activities.joinPolicyOpen')}</option>
              <option value="approval">{t('activities.joinPolicyApproval')}</option>
            </select>
          </FormField>
          {renderNav(!!name.trim())}
        </div>
      )}

      {step === 1 && (
        <div>
          <p className="form-help-text">{t('activity.wizard.templatesHint')}</p>
          <MultiSelect
            label={t('activity.wizard.stepTemplates')}
            options={templateOptions}
            selectedIds={selectedTemplateIds}
            onChange={setSelectedTemplateIds}
            placeholder={t('activity.wizard.noneSelected')}
          />
          {renderNav()}
        </div>
      )}

      {step === 2 && (
        <div>
          <p className="form-help-text">{t('activity.wizard.groupsHint')}</p>
          <MultiSelect
            label={t('activity.wizard.stepGroups')}
            options={groupOptions}
            selectedIds={selectedGroupIds}
            onChange={setSelectedGroupIds}
            placeholder={t('activity.wizard.noneSelected')}
          />
          {renderNav()}
        </div>
      )}

      {step === 3 && (
        <div>
          <p className="form-help-text">{t('activity.wizard.eventsHint')}</p>
          <MultiSelect
            label={t('activity.wizard.stepEvents')}
            options={eventOptions}
            selectedIds={selectedEventIds}
            onChange={setSelectedEventIds}
            placeholder={t('activity.wizard.noneSelected')}
          />
          {renderNav()}
        </div>
      )}

      {step === 4 && (
        <div>
          <p className="form-help-text">{t('activity.wizard.membersHint')}</p>
          <MultiSelect
            label={t('activity.wizard.stepMembers')}
            options={memberOptions}
            selectedIds={selectedMemberIds}
            onChange={setSelectedMemberIds}
            placeholder={t('activity.wizard.noneSelected')}
          />
          {renderNav()}
        </div>
      )}

      {step === 5 && (
        <div>
          <p className="form-help-text">{t('activity.wizard.reviewHint')}</p>
          <dl style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.5rem 1rem' }}>
            <dt>{t('activities.name')}</dt>
            <dd>{name || <em>—</em>}</dd>
            <dt>{t('activities.description')}</dt>
            <dd>{description || <em>—</em>}</dd>
            <dt>{t('activities.visibility')}</dt>
            <dd>{visibility}</dd>
            <dt>{t('activities.joinPolicy')}</dt>
            <dd>{joinPolicy}</dd>
            <dt>{t('activity.wizard.stepTemplates')}</dt>
            <dd>{t('activity.wizard.selectedCount', { count: selectedTemplateIds.length })}</dd>
            <dt>{t('activity.wizard.stepGroups')}</dt>
            <dd>{t('activity.wizard.selectedCount', { count: selectedGroupIds.length })}</dd>
            <dt>{t('activity.wizard.stepEvents')}</dt>
            <dd>{t('activity.wizard.selectedCount', { count: selectedEventIds.length })}</dd>
            <dt>{t('activity.wizard.stepMembers')}</dt>
            <dd>{t('activity.wizard.selectedCount', { count: selectedMemberIds.length })}</dd>
          </dl>
          {renderNav()}
        </div>
      )}
    </div>
  );
};

export default ActivityCreationWizard;
